/**
 * Global engagement instrumentation for the marketing site.
 *
 * One delegated click listener classifies every click and reports to GA4:
 *   - `cta_click`      key conversion CTAs (Request a consultation, Book a demo,
 *                      Start free, Request access, Talk to sales, Get pricing, ...)
 *   - `outbound_click` links leaving csquare hosts (partners, social, docs, G2, ...)
 *   - `contact_click`  mailto: / tel: links
 *   - `file_download`  pdf / doc / xls / csv / zip assets
 *
 * We use our own `outbound_click` name (not GA4's enhanced-measurement `click`)
 * so the two never double-count each other.
 *
 * Programmatic CTAs that are not anchors (modal openers, wizard buttons) can call
 * `trackCtaClick()` directly.
 */

import { relayIfGtagBlocked } from '@/lib/ga4Tracking';
import { isGaDebugMode, isProdHost } from '@/lib/analyticsHost';
import { canSendClientAnalytics } from '@/lib/europeVisitor';

const GA4_MEASUREMENT_ID = 'G-M09E85VL9J';

/** Hosts we consider "ours" — links to these are internal, not outbound. */
const INTERNAL_HOST_RE = /(^|\.)(csquaregrc\.com|csquare\.dev|lovable\.app|lovable\.dev|localhost)$/i;

const DOWNLOAD_EXT_RE = /\.(pdf|docx?|xlsx?|pptx?|csv|zip|json|md)(\?|#|$)/i;

/** Text / href patterns that mark a link or button as a key CTA. */
const CTA_PATTERNS: Array<{ id: string; re: RegExp }> = [
  { id: 'request_consultation', re: /request a consultation|book a consultation|free consultation|consultation/i },
  { id: 'book_demo', re: /book a demo|schedule a demo|see the platform|request a demo|\/book-demo/i },
  { id: 'start_free', re: /start free|get started free|try (it )?free|\/subscribe|\/start-free|\/signup|\/sign-up/i },
  { id: 'request_access', re: /request access|get the (guide|template|report|playbook)|download/i },
  { id: 'contact_sales', re: /talk to (sales|us)|contact sales|contact us|\/contact/i },
  { id: 'view_pricing', re: /see pricing|view pricing|get pricing|\/pricing/i },
  { id: 'roi_calculator', re: /roi calculator|calculate (your )?roi|\/roi/i },
  { id: 'sign_in', re: /sign in|log in|\/auth/i },
];

function sendGtagEvent(eventName: string, params: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  // Production-only: never emit analytics from preview/localhost.
  if (!isProdHost()) return;
  if (!canSendClientAnalytics()) return;
  try {
    // dataLayer push keeps any future GTM container in sync.
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...params });
  } catch {
    /* ignore */
  }
  if (typeof window.gtag !== 'function') return;
  try {
    window.gtag('event', eventName, {
      send_to: GA4_MEASUREMENT_ID,
      page_path: `${window.location.pathname}${window.location.search}`,
      page_location: window.location.href,
      ...(isGaDebugMode() ? { debug_mode: 1 } : {}),
      ...params,
    });
  } catch {
    /* analytics must never break a click */
  }
}

function inferLocation(el: HTMLElement): string {
  const sectionEl = el.closest('[data-cta-location], section, header, footer, nav');
  const explicit = (sectionEl as HTMLElement | null)?.dataset?.ctaLocation;
  if (explicit) return explicit;
  if (!sectionEl) return 'unknown';
  const tag = sectionEl.tagName.toLowerCase();
  if (tag === 'header' || tag === 'nav') return 'nav';
  if (tag === 'footer') return 'footer';
  const id = (sectionEl as HTMLElement).id?.toLowerCase();
  return id || 'section';
}

function matchCtaId(text: string, href: string): string | undefined {
  return CTA_PATTERNS.find((p) => p.re.test(text) || p.re.test(href))?.id;
}

export interface CtaClickParams {
  /** Stable CTA identifier, e.g. `request_consultation`. */
  cta_id: string;
  cta_text?: string;
  /** Section / page area, e.g. `hero`, `pricing`, `nav`. */
  cta_location?: string;
  cta_destination?: string;
  [key: string]: unknown;
}

/** Report a key CTA click to GA4 (client-side + Measurement Protocol relay). */
export function trackCtaClick(params: CtaClickParams): void {
  const payload = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
  );
  sendGtagEvent('cta_click', payload);
  // Server-side fallback only when gtag.js was blocked — never a duplicate.
  relayIfGtagBlocked('cta_click', { params: payload });
}

let installed = false;

export function installEngagementTracking(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  document.addEventListener(
    'click',
    (e) => {
      try {
        const target = e.target as HTMLElement | null;
        if (!target || typeof target.closest !== 'function') return;

        const el = target.closest(
          'a[href], button[data-cta-id], [role="button"][data-cta-id]',
        ) as HTMLElement | null;
        if (!el) return;

        const href = el.getAttribute('href') || '';
        const text = (el.textContent || el.getAttribute('aria-label') || '').trim().slice(0, 100);
        const location = el.dataset.ctaLocation || inferLocation(el);

        // mailto / tel
        if (/^(mailto|tel):/i.test(href)) {
          sendGtagEvent('contact_click', {
            method: href.toLowerCase().startsWith('tel:') ? 'phone' : 'email',
            link_url: href,
            link_text: text,
            cta_location: location,
          });
          return;
        }

        let url: URL | null = null;
        try {
          url = href ? new URL(href, window.location.href) : null;
        } catch {
          url = null;
        }

        const isOutbound = !!url && /^https?:$/.test(url.protocol) && !INTERNAL_HOST_RE.test(url.hostname);

        if (url && DOWNLOAD_EXT_RE.test(url.pathname)) {
          sendGtagEvent('file_download', {
            file_name: url.pathname.split('/').pop() || url.pathname,
            file_extension: (url.pathname.split('.').pop() || '').toLowerCase(),
            link_url: url.href,
            link_text: text,
            cta_location: location,
          });
          return;
        }

        if (isOutbound && url) {
          sendGtagEvent('outbound_click', {
            link_url: url.href,
            link_domain: url.hostname,
            link_text: text,
            cta_location: location,
            outbound: true,
          });
          return;
        }

        const ctaId = el.dataset.ctaId || matchCtaId(text, href);
        if (!ctaId) return;

        const plan = url?.searchParams.get('plan') || undefined;
        trackCtaClick({
          cta_id: ctaId,
          cta_text: text,
          cta_location: location,
          cta_destination: url ? url.pathname + url.search : href,
          cta_plan: plan,
        });
      } catch {
        /* never break a click */
      }
    },
    { capture: true },
  );
}
